import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import { motion, useReducedMotion, useScroll, useTransform, useMotionValueEvent } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

// Product-page-compliant ingredient list (EU Regulation 1924/2006).
const INGREDIENTS = [
    {
        name_en: 'Glycine',
        name_nl: 'Glycine',
        dose: '3000 mg',
        img: '/ingredients/glycine-h.webp',
        bg: '#E9EFFB',
        deskH: 54,
        desc_en: 'A non-essential amino acid the body makes itself, also found in protein-rich foods.',
        desc_nl: 'Een niet-essentieel aminozuur dat het lichaam zelf aanmaakt en voorkomt in eiwitrijke voeding.',
    },
    {
        name_en: 'Magnesium bisglycinate',
        name_nl: 'Magnesiumbisglycinaat',
        dose: '200 mg',
        dose_note_en: 'elemental',
        dose_note_nl: 'elementair',
        img: '/ingredients/magnesium-h.webp',
        bg: '#FBE9F0',
        deskH: 66,
        desc_en: 'A chelated form of the essential mineral magnesium, bound to the amino acid glycine.',
        desc_nl: 'Een gechelateerde vorm van het essentiële mineraal magnesium, gebonden aan het aminozuur glycine.',
        hide_claim_mobile: true,
        claim_en: 'Magnesium contributes to the reduction of tiredness and fatigue, to normal psychological function, and to the normal functioning of the nervous system.',
        claim_nl: 'Magnesium draagt bij tot de vermindering van vermoeidheid en moeheid, tot een normale psychologische functie en tot de normale werking van het zenuwstelsel.',
    },
    {
        name_en: 'L-Theanine',
        name_nl: 'L-Theanine',
        dose: '200 mg',
        img: '/ingredients/theanine-h.webp',
        bg: '#E5F0F7',
        deskH: 60,
        desc_en: 'An amino acid that occurs naturally in the leaves of the tea plant (Camellia sinensis).',
        desc_nl: 'Een aminozuur dat van nature voorkomt in de bladeren van de theeplant (Camellia sinensis).',
    },
    {
        name_en: 'Lemon Balm',
        name_nl: 'Citroenmelisse',
        dose: '200 mg',
        dose_note_en: 'leaf extract',
        dose_note_nl: 'blad-extract',
        img: '/ingredients/lemonbalm-h.webp',
        bg: '#FDF1F5',
        deskH: 64,
        fadeRight: true,
        desc_en: 'An extract of Melissa officinalis, a herb from the mint family native to southern Europe. Standardised to at least 2.5% hydroxycinnamic acid derivatives.',
        desc_nl: 'Een extract van Melissa officinalis, een kruid uit de muntfamilie dat oorspronkelijk uit Zuid-Europa komt. Gestandaardiseerd op minimaal 2,5% hydroxykaneelzuurderivaten.',
    },
    {
        name_en: 'Chicory inulin',
        name_nl: 'Cichorei-inuline',
        dose: '400 mg',
        dose_note_en: 'carrier',
        dose_note_nl: 'drager',
        img: '/ingredients/chicory-h.webp',
        bg: '#F5EFE6',
        deskH: 38,
        fadeRight: true,
        desc_en: 'A soluble fibre from chicory root, used as a natural carrier.',
        desc_nl: 'Een oplosbare vezel uit de cichoreiwortel, gebruikt als natuurlijke drager.',
    },
];

const slug = (ing) => ing.name_en.replace(/\s+/g, '-').toLowerCase();
const pick = (ing, key, lang) => ing[`${key}_${lang}`] || ing[`${key}_en`];
const doseFor = (ing, lang) => ing[`dose_${lang}`] || ing.dose || ing.dose_en;

const LAST = INGREDIENTS.length - 1;

function Layer({ ing, i, x, reduce }) {
    const opacity = useTransform(x, [i - 0.75, i - 0.25, i + 0.25, i + 0.75], [0, 1, 1, 0]);
    const y = useTransform(x, [i - 1, i, i + 1], reduce ? [0, 0, 0] : [70, 0, -70]);
    const rotate = useTransform(x, [i - 1, i, i + 1], reduce ? [0, 0, 0] : [-4, 0, 3]);
    return (
        <motion.div
            style={{ opacity, y, rotate, '--h': `${ing.deskH}svh` }}
            className="absolute inset-0 flex items-center justify-center lg:justify-start lg:inset-x-0 lg:top-16 lg:bottom-16"
        >
            <motion.img
                src={ing.img}
                alt=""
                aria-hidden="true"
                animate={reduce ? undefined : { y: [0, -12, 0] }}
                transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
                className={`max-h-full max-w-full object-contain lg:w-auto lg:max-w-none lg:h-[var(--h)] ${ing.fadeRight ? 'lg:[mask-image:linear-gradient(to_right,#000_78%,transparent)]' : ''}`}
                data-testid={`ingredient-illustration-${slug(ing)}`}
            />
        </motion.div>
    );
}

function Backdrop({ x, reduce }) {
    const backgroundColor = useTransform(x, INGREDIENTS.map((_, i) => i), INGREDIENTS.map((g) => g.bg));
    return (
        <motion.div
            className="sticky top-0 h-[100svh] overflow-hidden"
            style={{ backgroundColor }}
            data-testid="ingredients-backdrop"
        >
            <div className="absolute inset-x-0 bottom-0" style={{ top: 'var(--page-offset, 128px)' }}>
                <div className="section-container relative h-full">
                    <div className="absolute left-[calc(50%-50vw)] right-[calc(50%-50vw)] top-12 h-[30svh] [@media(max-height:700px)]:top-6 [@media(max-height:700px)]:h-[20svh] lg:top-0 lg:bottom-0 lg:h-auto">
                        {INGREDIENTS.map((ing, i) => (
                            <Layer key={ing.img} ing={ing} i={i} x={x} reduce={reduce} />
                        ))}
                    </div>
                </div>
            </div>
        </motion.div>
    );
}

function RailOverlay(props) {
    return (
        <div className="absolute inset-0 z-20 pointer-events-none">
            <div className="sticky top-0 h-[100svh]">
                <div className="absolute inset-x-0 bottom-0" style={{ top: 'var(--page-offset, 128px)' }}>
                    <div className="section-container relative h-full">
                        <Rail {...props} />
                    </div>
                </div>
            </div>
        </div>
    );
}

function Rail({ active, lang, onJump }) {
    return (
        <div className="hidden lg:block absolute lg:right-8 lg:bottom-10 pointer-events-auto" data-testid="ingredients-rail">
            <div className="flex gap-1.5 lg:gap-6">
                {INGREDIENTS.map((ing, i) => (
                    <button
                        key={ing.img}
                        onClick={() => onJump(i)}
                        className="group flex-1 lg:flex-none flex flex-col-reverse items-start gap-2 text-left py-3 lg:py-0"
                        aria-label={pick(ing, 'name', lang)}
                        data-testid={`ingredients-rail-${slug(ing)}`}
                    >
                        <span className={`hidden lg:inline font-condensed font-bold uppercase text-[11px] tracking-[0.18em] transition-colors duration-300 ${i === active ? 'text-primary' : 'text-slate-500 group-hover:text-slate-700'}`}>
                            {pick(ing, 'name', lang)}
                        </span>
                        <span className={`block h-[3px] rounded-full transition-[width,background-color] duration-500 w-full ${i === active ? 'bg-secondary' : 'bg-primary/15'}`} />
                    </button>
                ))}
            </div>
        </div>
    );
}

function Panel({ ing, i, lang }) {
    const claim = pick(ing, 'claim', lang);
    const note = pick(ing, 'dose_note', lang);
    return (
        <article
            data-ingredient-panel
            className="flex"
            data-testid={`ingredient-${slug(ing)}`}
        >
            <div className="section-container w-full">
                <div
                    data-ingredient-card
                    className="pointer-events-auto max-w-md rounded-[22px] p-5 sm:p-6 lg:p-8 bg-white/30 backdrop-blur-2xl backdrop-saturate-150 border border-white/60 shadow-[0_8px_32px_rgba(15,23,42,0.08),inset_0_1px_0_rgba(255,255,255,0.6)]"
                    data-testid={`ingredient-card-${slug(ing)}`}
                >
                    <div className="flex items-center gap-3 mb-4 lg:mb-6">
                        <span className="font-condensed font-bold text-sm text-secondary-dark">0{i + 1}</span>
                        <span className="w-10 h-px bg-secondary-dark/40" />
                        {ing.dose && (
                            <span className="font-condensed font-bold uppercase text-[11px] tracking-[0.2em] text-slate-500">
                                {lang === 'nl' ? 'per dosering' : 'per serving'}
                            </span>
                        )}
                    </div>
                    <div className="flex items-baseline gap-3 flex-wrap">
                        <div className="font-condensed font-extrabold uppercase text-primary text-5xl sm:text-7xl xl:text-8xl leading-[0.85] tracking-tight" data-testid={`ingredient-dose-${slug(ing)}`}>
                            {doseFor(ing, lang)}
                        </div>
                        {note && (
                            <span className="font-condensed font-bold uppercase text-secondary-dark text-xs tracking-[0.18em]" data-testid={`ingredient-dose-note-${slug(ing)}`}>
                                {note}
                            </span>
                        )}
                    </div>
                    <h3 className="font-condensed font-bold uppercase text-foreground text-2xl sm:text-3xl xl:text-4xl leading-none tracking-tight mt-3">
                        {pick(ing, 'name', lang)}
                    </h3>
                    <p className="text-sm sm:text-base text-slate-700 leading-relaxed mt-4 lg:mt-6">{pick(ing, 'desc', lang)}</p>
                    {claim && (
                        <p className={`text-xs text-muted-foreground mt-4 italic leading-relaxed ${ing.hide_claim_mobile ? 'hidden lg:block' : ''}`}>{claim}</p>
                    )}
                </div>
            </div>
        </article>
    );
}

export function ProductIngredientsSection() {
    const { lang } = useLanguage();
    const reduce = useReducedMotion();
    const stageRef = useRef(null);
    const { scrollY } = useScroll();
    // Scroll positions at which each card's centre crosses the focus line (lower on mobile, below the illustration).
    const [stops, setStops] = useState(() => INGREDIENTS.map((_, i) => i));
    useEffect(() => {
        const measure = () => {
            const focus = window.innerHeight * (window.innerWidth >= 1024 ? 0.5 : 0.66);
            const cards = stageRef.current?.querySelectorAll('[data-ingredient-card]') || [];
            const next = Array.from(cards).map((el) => {
                const r = el.getBoundingClientRect();
                return r.top + window.scrollY + r.height / 2 - focus;
            });
            if (next.length === INGREDIENTS.length) setStops(next);
        };
        measure();
        let raf = 0;
        const ro = new ResizeObserver(() => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(measure);
        });
        if (stageRef.current) ro.observe(stageRef.current);
        ro.observe(document.body);
        window.addEventListener('resize', measure);
        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [lang]);
    const x = useTransform(scrollY, stops, INGREDIENTS.map((_, i) => i));
    const [active, setActive] = useState(0);
    useMotionValueEvent(x, 'change', (v) => setActive(Math.min(LAST, Math.max(0, Math.round(v)))));

    const jump = (i) => window.scrollTo({ top: stops[i], behavior: 'smooth' });

    return (
        <section id="ingredients" style={{ scrollMarginTop: '120px', background: INGREDIENTS[0].bg }}>
            <div className="section-container pt-20 md:pt-28 pb-2">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="max-w-2xl"
                >
                    <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold text-primary mb-5">
                        <span className="w-8 h-px bg-primary" />
                        {lang === 'nl' ? 'De formule' : 'The formula'}
                    </div>
                    <h2 className="heading-lg text-foreground mb-4">
                        {lang === 'nl' ? '5 functionele ingrediënten.' : '5 functional ingredients.'}
                    </h2>
                    <p className="text-sm text-muted-foreground leading-relaxed mt-3">
                        {lang === 'nl'
                            ? 'Elke hoeveelheid heeft een reden. Op onze science-pagina zie je het onderzoek en de afwegingen achter de formule.'
                            : 'Every amount is there for a reason. Our Science page covers the research and trade-offs behind the formula.'}
                    </p>
                </motion.div>
            </div>

            <div ref={stageRef} className="relative" data-testid="ingredients-stage" data-active={active}>
                <Backdrop x={x} reduce={reduce} />
                <RailOverlay active={active} lang={lang} onJump={jump} />
                <div className="relative z-10 -mt-[100svh] flex flex-col gap-[30svh] lg:gap-[34svh] pt-[calc(66svh-150px)] pb-[calc(34svh-130px)] lg:pt-[calc(50svh-170px)] lg:pb-[calc(50svh-170px)] pointer-events-none">
                    {INGREDIENTS.map((ing, i) => (
                        <Panel key={ing.img} ing={ing} i={i} lang={lang} />
                    ))}
                </div>
            </div>

            <div style={{ background: INGREDIENTS[INGREDIENTS.length - 1].bg }}>
              <div className="section-container pt-6 pb-12 md:pt-8 md:pb-14">
                <Link
                    to="/science"
                    className="inline-flex items-center gap-2 text-primary font-semibold text-sm hover:underline"
                    data-testid="ingredients-science-link"
                >
                    {lang === 'nl'
                        ? 'Benieuwd naar de wetenschap achter deze ingrediënten?'
                        : 'Curious about the science behind these ingredients?'}
                    <ArrowRight size={14} />
                </Link>
              </div>
            </div>
        </section>
    );
}
